/* global BigInt */
const _te = new TextEncoder();

/**
 * Convert a string to UTF-8 encoded Uint8Array.
 * @param {string} s - The string to encode
 * @returns {Uint8Array} The UTF-8 encoded bytes
 */
export const utf8 = (s) => _te.encode(s);

/**
 * Concatenate multiple Uint8Array instances into a single Uint8Array.
 * @param {...Uint8Array} arrs - The arrays to concatenate
 * @returns {Uint8Array} A new array containing all input arrays concatenated
 */
export function concatBytes(...arrs) {
  const total = arrs.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

/**
 * Convert a BigInt to a big-endian byte array of fixed length.
 * @param {bigint} n - The BigInt to convert
 * @param {number} len - The desired byte length (will be zero-padded if necessary)
 * @returns {Uint8Array} The big-endian byte representation
 * @throws {Error} If the BigInt is too large for the specified length
 */
export const bigIntToBytes = (n, len) => {
  let hex = n.toString(16);
  if (hex.length % 2) hex = '0' + hex;
  const raw = Uint8Array.from(hex.match(/../g).map(h => parseInt(h, 16)));
  if (raw.length > len) throw new Error('bigIntToBytes overflow');
  const out = new Uint8Array(len);
  out.set(raw, len - raw.length);
  return out;
};

/**
 * Convert a big-endian byte array to a BigInt.
 * @param {Uint8Array} u8 - The byte array to convert
 * @returns {bigint} The BigInt representation
 */
export const bytesToBigInt = (u8) => { let v = 0n; for (const b of u8) v = (v << 8n) | BigInt(b); return v; };

/**
 * Securely clear sensitive data from memory.
 * Note: JavaScript strings are immutable and cannot be securely cleared.
 * Only Uint8Array data can be reliably zeroed out.
 * @param {Uint8Array} data - The data to clear
 */
const secureClear = (data) => {
  if (data instanceof Uint8Array) {
    data.fill(0);
  }
  // Note: Strings are immutable in JavaScript and cannot be cleared from memory.
  // Callers should avoid storing sensitive data as strings when possible.
};

/**
 * J-PAKE (Password Authenticated Key Exchange by Juggling) Protocol Implementation.
 * Implements RFC 8236 with additional security features including:
 * - Zero-knowledge proofs for each round
 * - Key confirmation using HMAC
 * - HKDF-based key derivation
 * - AES-256-GCM authenticated encryption
 *
 * Protocol State Machine:
 *   init -> round1_generated -> round1_processed -> round2_generated ->
 *   round2_processed -> keys_calculated -> (ready for encryption/decryption)
 */
export class JPAKEProtocol {
  /**
   * Create a new J-PAKE protocol instance.
   * @param {Uint8Array} passwordHash - Pre-hashed password (SHA-256)
   * @param {Uint8Array} contextBytes - Session context binding (sessionId|room|users)
   * @param {boolean} isAlice - True if this party is Alice (initiator), false if Bob
   * @param {string} aliceName - Alice's username (for ZKP binding per RFC 8236)
   * @param {string} bobName - Bob's username (for ZKP binding per RFC 8236)
   */
  constructor(passwordHash, contextBytes, isAlice, aliceName, bobName) {
    this.passwordHash = new Uint8Array(passwordHash); // Create a copy
    this.contextBytes = contextBytes; // binds sessionId|room|users into ZKPs, AAD, HKDF
    this.isAlice = !!isAlice;         // role determined by sorted usernames
    this.aliceName = aliceName;
    this.bobName = bobName;

    // MODP 2048-bit (RFC 3526 group 14)
    this.p = BigInt('0x' +
      'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD1' +
      '29024E088A67CC74020BBEA63B139B22514A08798E3404DD' +
      'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245' +
      'E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
      'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3D' +
      'C2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F' +
      '83655D23DCA3AD961C62F356208552BB9ED529077096966D' +
      '670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
      'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9' +
      'DE2BCBF6955817183995497CEA956AE515D2261898FA0510' +
      '15728E5A8AACAA68FFFFFFFFFFFFFFFF');

    this.q = (this.p - 1n) / 2n;
    // q-subgroup generator g = 2^2 mod p (then verify)
    this.g = this.modPow(2n, 2n, this.p);
    if (this.modPow(this.g, this.q, this.p) !== 1n || this.g === 1n) throw new Error('Bad generator');
    this.intLen = ((this.p.toString(16).length + 1) >> 1);

    this.x1 = null; this.x2 = null; // our exponents (x1 may be 0; x2 != 0)
    this.g1 = null; this.g2 = null; // our round-1 values
    this.peerG3 = null; this.peerG4 = null; // peer round-1 values
    this.g3 = null; this.g4 = null; // our combined (g3) and round-2 (A or B) value (g4)
    this.peerRound2 = null;          // peer A/B value

    this.sessionKey = null; this.encKey = null; this.macKey = null;
    this.seenIvs = new Set(); this.txCtr = 0n; this.rxCtrMax = -1n;

    // transcript parts are appended AFTER verifies to ensure both sides hash the same bytes
    this._txParts = [];
    this._r1_out = null; this._r2_out = null; // staged until counterpart verified

    // Track protocol state to prevent replay/out-of-order attacks
    this.state = 'init';
  }

  // ----- math -----
  generateRandomExponent() { // non-zero
    const blen = this.intLen;
    while (true) {
      const r = new Uint8Array(blen); crypto.getRandomValues(r);
      let x = 0n; for (const b of r) x = (x << 8n) | BigInt(b);
      x = x % this.q; if (x !== 0n) return x;
    }
  }
  generateRandomExponentAllowZero() { // may be zero
    const blen = this.intLen;
    const r = new Uint8Array(blen); crypto.getRandomValues(r);
    let x = 0n; for (const b of r) x = (x << 8n) | BigInt(b);
    return x % this.q;
  }
  inQSubgroup(y) { return (typeof y === 'bigint') && y >= 1n && y < this.p && this.modPow(y, this.q, this.p) === 1n; }
  modPow(base, exp, mod) {
    if (mod === 1n) return 0n;
    let r = 1n;
    base %= mod;
    while (exp > 0n) {
      if (exp & 1n) r = (r * base) % mod;
      exp >>= 1n;
      base = (base * base) % mod;
    }
    return r;
  }
  modInverse(a, m) {
    const eg = (a, b) => {
      if (a === 0n) return [b, 0n, 1n];
      const [g, x1, y1] = eg(b % a, a);
      return [g, y1 - (b / a) * x1, x1];
    };
    const [g, x] = eg(a % m, m);
    if (g !== 1n) throw new Error('no inverse');
    return (x % m + m) % m;
  }

  // ----- transcript -----
  _push(tagBytes, ...elts) { this._txParts.push(concatBytes(tagBytes, ...elts)); }
  async _txHash() { const data = this._txParts.length ? concatBytes(...this._txParts) : new Uint8Array(0); const h = await crypto.subtle.digest('SHA-256', data); return new Uint8Array(h); }
  encodeCtr(ctr) { const out = new Uint8Array(8); for (let i = 7; i >= 0; i--) { out[i] = Number(ctr & 0xffn); ctr >>= 8n; } return out; }

  // ----- ZKP challenge (binds context + transcript-so-far + UserIDs per RFC 8236 §2.2) -----
  async challengeHash(generator, value, V, proverID, verifierID, label) {
    const L = this.intLen; const th = await this._txHash();
    const h = await crypto.subtle.digest('SHA-256', concatBytes(
      bigIntToBytes(generator, L),       // g (base generator)
      bigIntToBytes(V, L),                // V = g^v (commitment)
      bigIntToBytes(value, L),            // D = g^d (the value being proven)
      utf8(proverID),                     // UserID of prover (RFC 8236 requirement)
      utf8(verifierID),                   // UserID of verifier (for binding)
      this.contextBytes,                  // Session context
      th,                                 // Transcript hash
      utf8(label)                         // Protocol phase label
    ));
    return bytesToBigInt(new Uint8Array(h)) % this.q;
  }
  async generateZKP(exponent, generator, value, proverID, verifierID, label) {
    const v = this.generateRandomExponentAllowZero();
    const V = this.modPow(generator, v, this.p);
    const h = await this.challengeHash(generator, value, V, proverID, verifierID, label);
    let r = (v - (exponent * h)) % this.q;
    if (r < 0n) r += this.q;
    return { V: V.toString(16), r: r.toString(16) };
  }
  async verifyZKP(generator, value, zkp, proverID, verifierID, label) {
    // RFC 8236 §2.2: Verify prover's UserID is different from verifier's identity
    if (proverID === verifierID) return false;

    const V = BigInt('0x' + zkp.V);
    const r = BigInt('0x' + zkp.r);
    // Verify V is in the correct subgroup
    if (!this.inQSubgroup(V)) return false;

    const h = await this.challengeHash(generator, value, V, proverID, verifierID, label);
    const gR = this.modPow(generator, r, this.p);
    const xH = this.modPow(value, h, this.p);
    return ((gR * xH) % this.p) === V;
  }

  // ----- Round 1 -----
  /**
   * Generate Round 1 values (g^x1, g^x2) with zero-knowledge proofs.
   * RFC 8236: x1 ∈ [0,q-1], x2 ∈ [1,q-1]
   * @returns {Promise<Object>} Round 1 data to send to peer: {g1, g2, zkp1, zkp2}
   * @throws {Error} If not in 'init' state or values not in subgroup
   */
  async generateRound1() {
    if (this.state !== 'init') throw new Error('Invalid state for round1 generation');

    // RFC 8236: x1 ∈ [0,q-1], x2 ∈ [1,q-1]
    this.x1 = this.generateRandomExponentAllowZero();
    this.x2 = this.generateRandomExponent();
    this.g1 = this.modPow(this.g, this.x1, this.p);
    this.g2 = this.modPow(this.g, this.x2, this.p);

    // Verify values are in the correct subgroup
    if (!this.inQSubgroup(this.g1) || !this.inQSubgroup(this.g2)) {
      throw new Error('Generated values not in subgroup');
    }

    // Generate ZKPs with proverID (our name) and verifierID (peer name)
    const myName = this.isAlice ? this.aliceName : this.bobName;
    const peerName = this.isAlice ? this.bobName : this.aliceName;
    const zkp1 = await this.generateZKP(this.x1, this.g, this.g1, myName, peerName, 'round1/x1');
    const zkp2 = await this.generateZKP(this.x2, this.g, this.g2, myName, peerName, 'round1/x2');
    // stage R1|OUT; we will append after we verify peer R1 so both sides hash same bytes
    this._r1_out = { g1: this.g1, g2: this.g2, zkp1, zkp2 };
    this.state = 'round1_generated';
    return { g1: this.g1.toString(16), g2: this.g2.toString(16), zkp1, zkp2 };
  }

  /**
   * Process peer's Round 1 data and verify zero-knowledge proofs.
   * Updates canonical transcript with both parties' Round 1 values.
   * @param {Object} peerData - Peer's Round 1 data {g1, g2, zkp1, zkp2}
   * @returns {Promise<boolean>} True if verification successful
   * @throws {Error} If ZKPs fail or values not in subgroup
   */
  async processRound1(peerData) {
    if (this.state !== 'round1_generated') throw new Error('Invalid state for round1 processing');

    // map peer g1,g2 -> RFC g3,g4 from our perspective
    this.peerG3 = BigInt('0x' + peerData.g1);
    this.peerG4 = BigInt('0x' + peerData.g2);

    if (!this.inQSubgroup(this.peerG3) || !this.inQSubgroup(this.peerG4)) {
      throw new Error('Peer R1 not in subgroup');
    }
    if (this.peerG4 === 1n) throw new Error('Peer second generator is identity');

    // Verify ZKPs with proverID (peer name) and verifierID (our name)
    const myName = this.isAlice ? this.aliceName : this.bobName;
    const peerName = this.isAlice ? this.bobName : this.aliceName;
    if (!await this.verifyZKP(this.g, this.peerG3, peerData.zkp1, peerName, myName, 'round1/x1')) {
      throw new Error('R1 ZKP1 fail');
    }
    if (!await this.verifyZKP(this.g, this.peerG4, peerData.zkp2, peerName, myName, 'round1/x2')) {
      throw new Error('R1 ZKP2 fail');
    }

    // --- Canonical R1 transcript: always [Alice's g1,g2] then [Bob's g3,g4] ---
    // CRITICAL: Both parties must hash the same transcript in the same order.
    // The transcript is always ordered as Alice's values first, then Bob's values,
    // regardless of which party received which values first. This ensures both
    // parties compute identical transcript hashes for HKDF and ZKP challenges.
    const L = this.intLen;
    if (!this._r1_out) throw new Error('Local R1 not generated');

    const ours = this._r1_out; // { g1, g2, zkp1, zkp2 }
    if (this.isAlice) {
      // we are Alice: ours first as R1|A, then peer as R1|B
      this._push(utf8('R1|A'),
        bigIntToBytes(ours.g1, L), bigIntToBytes(ours.g2, L),
        utf8(ours.zkp1.V), utf8(ours.zkp1.r),
        utf8(ours.zkp2.V), utf8(ours.zkp2.r)
      );
      this._push(utf8('R1|B'),
        bigIntToBytes(this.peerG3, L), bigIntToBytes(this.peerG4, L),
        utf8(peerData.zkp1.V), utf8(peerData.zkp1.r),
        utf8(peerData.zkp2.V), utf8(peerData.zkp2.r)
      );
    } else {
      // we are Bob: peer (Alice) first as R1|A, then ours as R1|B
      this._push(utf8('R1|A'),
        bigIntToBytes(this.peerG3, L), bigIntToBytes(this.peerG4, L),
        utf8(peerData.zkp1.V), utf8(peerData.zkp1.r),
        utf8(peerData.zkp2.V), utf8(peerData.zkp2.r)
      );
      this._push(utf8('R1|B'),
        bigIntToBytes(ours.g1, L), bigIntToBytes(ours.g2, L),
        utf8(ours.zkp1.V), utf8(ours.zkp1.r),
        utf8(ours.zkp2.V), utf8(ours.zkp2.r)
      );
    }

    this.state = 'round1_processed';
    return true;
  }

  // map passwordHash bytes into Z_q scalar using HKDF to ensure proper distribution
  async passwordHashToScalar() {
    const info = utf8('JPAKE-pw-to-scalar');
    const salt = utf8('lockstep/v1:pw-map');
    const baseKey = await crypto.subtle.importKey('raw', this.passwordHash, 'HKDF', false, ['deriveBits']);

    // Calculate the number of bytes needed to represent q, then convert to bits
    const bitLength = this.q.toString(2).length;
    // Round up to the nearest multiple of 8
    const derivedBitLength = Math.ceil(bitLength / 8) * 8;

    const bits = await crypto.subtle.deriveBits(
      { name: 'HKDF', hash: 'SHA-256', salt, info },
      baseKey,
      derivedBitLength
    );

    const derivedBytes = new Uint8Array(bits);
    const x = bytesToBigInt(derivedBytes) % (this.q - 1n);
    return x + 1n; // ensure s in [1, q-1]
  }

  // ----- Round 2 -----
  /**
   * Generate Round 2 value (A or B) with zero-knowledge proof.
   * Computes combined generator and raises it to x2*s where s is password-derived scalar.
   * @returns {Promise<Object>} Round 2 data to send to peer: {A, zkpA}
   * @throws {Error} If not in correct state or combined generator invalid
   */
  async generateRound2() {
    if (this.state !== 'round1_processed') throw new Error('Invalid state for round2 generation');
    if (!this.g1 || !this.peerG3 || !this.peerG4 || !this.x2) throw new Error('R2 preconditions');

    // RFC 8236 §2.3: Calculate combined generator based on role.
    // Alice computes: combined = (g^x1) * (g^x3) * (g^x4) = g^(x1+x3+x4)
    // Bob computes:   combined = (g^x1) * (g^x2) * (g^x3) = g^(x1+x2+x3)
    // These are different generators, which is correct per the protocol.
    // Alice will compute A = combined^(x2*s), Bob will compute B = combined^(x4*s).
    const combined = this.isAlice ?
      ((this.g1 * this.peerG3 % this.p) * this.peerG4 % this.p) :
      ((this.peerG3 * this.peerG4 % this.p) * this.g1 % this.p);

    if (!this.inQSubgroup(combined) || combined === 1n) throw new Error('Bad combined generator');

    const s = await this.passwordHashToScalar();
    const x2s = (this.x2 * s) % this.q;
    const AorB = this.modPow(combined, x2s, this.p);

    // Verify the result is in the correct subgroup and not identity
    if (!this.inQSubgroup(AorB) || AorB === 1n) throw new Error('AorB not valid');

    // Generate ZKP with proverID (our name) and verifierID (peer name)
    const myName = this.isAlice ? this.aliceName : this.bobName;
    const peerName = this.isAlice ? this.bobName : this.aliceName;
    const zkp = await this.generateZKP(x2s, combined, AorB, myName, peerName, 'round2/x2s');
    this.g3 = combined;
    this.g4 = AorB;
    this._r2_out = { val: AorB, zkp };
    this.state = 'round2_generated';

    return { A: AorB.toString(16), zkpA: zkp };
  }

  /**
   * Process peer's Round 2 data, verify ZKP, and derive session keys.
   * Calculates shared secret K, derives encryption and MAC keys via HKDF.
   * @param {Object} peerData - Peer's Round 2 data {A, zkpA}
   * @returns {Promise<Uint8Array>} The derived encryption key
   * @throws {Error} If ZKP fails or shared secret not in subgroup
   */
  async processRound2(peerData) {
    if (this.state !== 'round2_generated') throw new Error('Invalid state for round2 processing');

    this.peerRound2 = BigInt('0x' + peerData.A);

    // Verify the peer value is in the correct subgroup
    if (!this.inQSubgroup(this.peerRound2)) {
      throw new Error('Peer R2 not in subgroup');
    }

    // Calculate the combined generator the peer should have used
    // If we are Alice, peer is Bob → Bob used (g1*g2*g3)^(x4*s)
    // If we are Bob, peer is Alice → Alice used (g1*g3*g4)^(x2*s)
    const peerCombined = this.isAlice
      ? ((this.g1 * this.g2 % this.p) * this.peerG3 % this.p)  // Alice verifying Bob's B: g1*g2*g3
      : ((this.peerG3 * this.g1 % this.p) * this.g2 % this.p); // Bob verifying Alice's A: g1*g3*g4

    if (!this.inQSubgroup(peerCombined)) {
      throw new Error('Peer combined not in subgroup');
    }

    // Verify ZKP with proverID (peer name) and verifierID (our name)
    const myName = this.isAlice ? this.aliceName : this.bobName;
    const peerName = this.isAlice ? this.bobName : this.aliceName;
    if (!await this.verifyZKP(peerCombined, this.peerRound2, peerData.zkpA, peerName, myName, 'round2/x2s')) {
      throw new Error('R2 ZKP fail');
    }

    // --- Canonical R2 transcript ---
    if (!this._r2_out) throw new Error('Local R2 not generated');
    const L = this.intLen;
    const my = this._r2_out;
    if (this.isAlice) {
      this._push(utf8('R2|A'),
        bigIntToBytes(my.val, L), utf8(my.zkp.V), utf8(my.zkp.r)
      );
      this._push(utf8('R2|B'),
        bigIntToBytes(this.peerRound2, L), utf8(peerData.zkpA.V), utf8(peerData.zkpA.r)
      );
    } else {
      this._push(utf8('R2|A'),
        bigIntToBytes(this.peerRound2, L), utf8(peerData.zkpA.V), utf8(peerData.zkpA.r)
      );
      this._push(utf8('R2|B'),
        bigIntToBytes(my.val, L), utf8(my.zkp.V), utf8(my.zkp.r)
      );
    }

    this.state = 'round2_processed';
    return await this.calculateSessionKey();
  }

  // ----- Key derivation per RFC: Ka/Kb and HKDF over transcript -----
  async calculateSessionKey() {
    const s = await this.passwordHashToScalar();
    const x2s = (this.x2 * s) % this.q;
    let k;

    if (this.isAlice) {
      // K = (B / g4^(x2*s))^x2
      const divisor = this.modPow(this.peerG4, x2s, this.p);
      const inv = this.modInverse(divisor, this.p);
      const adjusted = (this.peerRound2 * inv) % this.p;
      k = this.modPow(adjusted, this.x2, this.p);
    } else {
      // K = (A / g2^(x2*s))^x2, where g2 is Alice's second value (peerG4)
      const divisor = this.modPow(this.peerG4, x2s, this.p);
      const inv = this.modInverse(divisor, this.p);
      const adjusted = (this.peerRound2 * inv) % this.p;
      k = this.modPow(adjusted, this.x2, this.p);
    }

    if (!this.inQSubgroup(k)) throw new Error('Session key not in subgroup');

    const { encKey, macKey } = await this.deriveKeysHKDF(k);
    this.encKey = encKey;
    this.macKey = macKey;
    this.kcKey = await this.deriveKCKey(k);
    this.sessionKey = encKey;

    this.state = 'keys_calculated';
    return this.encKey;
  }

  async deriveKeysHKDF(k) {
    const L = this.intLen;
    const ikm = bigIntToBytes(k, L);
    const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
    const salt = await this._txHash(); // bind full transcript
    const info = utf8('lockstep/v1:enc|mac');
    const bits = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info }, baseKey, 512);
    const out = new Uint8Array(bits);
    return { encKey: out.slice(0, 32), macKey: out.slice(32, 64) };
  }

  // Derive dedicated key for key confirmation (k')
  async deriveKCKey(k) {
    const L = this.intLen;
    const ikm = bigIntToBytes(k, L);
    const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
    const salt = await this._txHash();
    const info = utf8('lockstep/v1:kc');
    const bits = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info }, baseKey, 256);
    return new Uint8Array(bits);
  }

  // ----- RFC-style KC_1_U MAC (two-step to avoid deadlock) -----
  async kcTagKC1U(isAliceTag) {
    const L = this.intLen;
    // Map values consistently to protocol roles: Alice's g1,g2 and Bob's g3,g4
    const a1 = this.isAlice ? this.g1 : this.peerG3; // Alice's g1
    const a2 = this.isAlice ? this.g2 : this.peerG4; // Alice's g2
    const b3 = this.isAlice ? this.peerG3 : this.g1; // Bob's g3
    const b4 = this.isAlice ? this.peerG4 : this.g2; // Bob's g4

    const kcKey = await crypto.subtle.importKey('raw', this.kcKey || this.macKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const aliceName = this.aliceName;
    const bobName = this.bobName;

    const data = isAliceTag
      ? concatBytes(
        utf8('KC_1_U'), utf8(aliceName), utf8(bobName),
        bigIntToBytes(a1, L), bigIntToBytes(a2, L),
        bigIntToBytes(b3, L), bigIntToBytes(b4, L)
      )
      : concatBytes(
        utf8('KC_1_U'), utf8(bobName), utf8(aliceName),
        bigIntToBytes(b3, L), bigIntToBytes(b4, L),
        bigIntToBytes(a1, L), bigIntToBytes(a2, L)
      );

    const sig = await crypto.subtle.sign('HMAC', kcKey, data);
    return btoa(String.fromCharCode(...new Uint8Array(sig)));
  }

  async kcVerifyKC1U(isPeerAliceTag, tagB64FromPeer) {
    const expected = await this.kcTagKC1U(isPeerAliceTag);
    if (expected.length !== tagB64FromPeer.length) {
      // Still compute to avoid timing leaks but return false
      await this.kcTagKC1U(isPeerAliceTag);
      return false;
    }

    // Constant-time comparison to prevent timing attacks
    let result = 0;
    for (let i = 0; i < expected.length; i++) {
      result |= expected.charCodeAt(i) ^ tagB64FromPeer.charCodeAt(i);
    }
    return result === 0;
  }

  // ----- AEAD -----
  /**
   * Encrypt a plaintext message using AES-256-GCM.
   * Uses counter-based nonce with random prefix and binds AAD to context+counter.
   * @param {string} plaintext - The message to encrypt
   * @returns {Promise<Object>} Object with {b64: base64-encoded ciphertext, ctr: counter string}
   * @throws {Error} If no session key has been established
   */
  async encrypt(plaintext) {
    if (!this.encKey) throw new Error('No session key established');

    this.txCtr += 1n;
    // AAD (Additional Authenticated Data) binds the ciphertext to:
    // 1. Session context (sessionId, room, participants)
    // 2. Message counter (prevents reordering/replay attacks)
    // This ensures the ciphertext can only be decrypted in the correct session
    // and at the correct position in the message sequence.
    const aad = concatBytes(this.contextBytes, this.encodeCtr(this.txCtr));

    // Use a combination of random and counter-based IV (96 bits total for GCM)
    // 4 bytes random prefix prevents any theoretical nonce collisions
    // 8 bytes counter ensures uniqueness within this session
    const iv = new Uint8Array(12);
    crypto.getRandomValues(iv.subarray(0, 4)); // 4 bytes random
    // Set remaining 8 bytes as counter
    const counterView = new DataView(iv.buffer, 4, 8);
    counterView.setBigUint64(0, this.txCtr);

    const key = await crypto.subtle.importKey('raw', this.encKey, { name: 'AES-GCM' }, false, ['encrypt']);
    const enc = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      key,
      utf8(plaintext)
    );

    const combined = new Uint8Array(iv.length + enc.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(enc), iv.length);

    return { b64: btoa(String.fromCharCode(...combined)), ctr: this.txCtr.toString() };
  }

  /**
   * Decrypt a ciphertext message using AES-256-GCM.
   * Verifies counter ordering to prevent replay attacks and checks for IV reuse.
   * @param {string} ciphertext - Base64-encoded ciphertext (includes IV)
   * @param {string} ctrStr - Counter value as string (for replay protection)
   * @returns {Promise<string>} The decrypted plaintext message
   * @throws {Error} If decryption fails, counter invalid, or IV reused
   */
  async decrypt(ciphertext, ctrStr) {
    if (!this.encKey) throw new Error('No session key established');

    const combined = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const enc = combined.slice(12);

    const ctr = ctrStr ? BigInt(ctrStr) : 0n;
    if (ctr <= this.rxCtrMax) throw new Error('Out-of-order/replay');
    this.rxCtrMax = ctr;

    const aad = concatBytes(this.contextBytes, this.encodeCtr(ctr));

    // Check for IV reuse using a limited-size set with expiration
    const ivKey = Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join('');
    if (this.seenIvs.has(ivKey)) throw new Error('IV reuse');

    // Limit the size of seen IVs to prevent memory exhaustion
    if (this.seenIvs.size > 1000) {
      this.seenIvs = new Set(Array.from(this.seenIvs).slice(-500));
    }
    this.seenIvs.add(ivKey);

    const key = await crypto.subtle.importKey('raw', this.encKey, { name: 'AES-GCM' }, false, ['decrypt']);
    const dec = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      key,
      enc
    );

    return new TextDecoder().decode(dec);
  }

  // Clean up sensitive data
  cleanup() {
    if (this.passwordHash) secureClear(this.passwordHash);
    if (this.encKey) secureClear(this.encKey);
    if (this.macKey) secureClear(this.macKey);
    if (this.sessionKey && this.sessionKey !== this.encKey) secureClear(this.sessionKey);

    // Clear bigints by setting to 0
    this.x1 = 0n;
    this.x2 = 0n;
    this.g1 = 0n;
    this.g2 = 0n;
    this.peerG3 = 0n;
    this.peerG4 = 0n;
    this.g3 = 0n;
    this.g4 = 0n;
    this.peerRound2 = 0n;
  }
}

// Expose JPAKEProtocol globally for performance testing
// This allows Playwright tests to instrument the class methods
if (typeof window !== 'undefined') {
  window.JPAKEProtocol = JPAKEProtocol;
}
